import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import xeRebook from 'actions/booking/xeRebook.action';
import ICO_CLOSE from 'assets/images/btn-close.svg';
import DatePickerInput from 'components/form/DatePickerInput.component';
import { useNavFor, useNavIsWeek } from 'hooks/useNav.hook';
import { usePathname } from 'hooks/usePathname.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectSchedulerDayView } from 'selectors/scheduler.selectors';
import { formatYearToDayCompact, formatYearToDayStandard } from 'utils/date/format.util';
import { nowAsMoment } from 'utils/date/now.util';
import { formatSchedulerWeekUrlSegment, getSchedulerWeekEndSunday, getSchedulerWeekStartMonday } from 'utils/date/schedulerWeekBound.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import { deepFreeze } from 'utils/values/constant.util';

import TextField from 'booking/components/common/TextField';
import { setCurrentDay, setView } from 'scheduler/actions';
import { REBOOK_TIME } from 'scheduler/components/common/RebookAppointmentDialog.component';

const L = logger('RebookAppointment');

export const RebookAppointment = ({ oldAppointment, from, showRebook, fetchProceduresBySalon, fetchEventsBySalon }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const nav = useNavFor('RebookAppointment');
  const pathname = usePathname();
  const isWeek = useNavIsWeek();
  const salonId = useCurrentSalonId();
  const dayView = useSelector(selectSchedulerDayView);

  const [selectedDate, setSelectedDate] = useState(nowAsMoment());
  const [showDatePicker] = useState(true);

  const stylistId = pathname.toLowerCase().indexOf('week/') > 0 ? parseInt(pathname.split('/').pop(), 10) : void 1;

  const setRebookDay = useCallback(
    (date) => {
      setSelectedDate(moment(date));

      if (nowAsMoment().isSame(moment(date), 'day')) return;
      if (from === REBOOK_TIME.before) deepFreeze(oldAppointment);

      const oldDate = moment(oldAppointment?.procedures?.[0]?.start_time).toISOString();
      const newDate = formatYearToDayStandard(date);

      dispatch(xeRebook(Object.assign({}, oldAppointment), from, newDate, oldDate));

      let url = `/salon/${salonId}/day/${formatYearToDayCompact(date)}/appointment/new/services`;

      if (isWeek) {
        const stylistId = parseInt(pathname.split('/').pop(), 10);
        const weekAnchor = moment(newDate);
        const bgn = getSchedulerWeekStartMonday(weekAnchor);
        const end = getSchedulerWeekEndSunday(weekAnchor);

        L.unignore('setRebookDay()', 'fetchProceduresBySalon:', fetchProceduresBySalon(salonId, bgn, end, stylistId));
        L.unignore('setRebookDay()', 'fetchEventsBySalon:', fetchEventsBySalon(salonId, bgn, end, stylistId));

        if (isWeek && stylistId) {
          url = `/salon/${salonId}/week/${formatSchedulerWeekUrlSegment(weekAnchor)}/${stylistId}/appointment/new/services`;
        }

        showRebook(false);
        nav.push(url);
      }
    },
    [dispatch, from, oldAppointment, pathname, salonId, isWeek, showRebook, nav, fetchProceduresBySalon, fetchEventsBySalon],
  );

  const handleDateChange = useCallback(
    (date) => {
      const startOfDay = date.startOf('day');

      dispatch(setCurrentDay(startOfDay));
      dispatch(setView(dayView, startOfDay, stylistId));
      setRebookDay(startOfDay);
    },
    [dispatch, dayView, stylistId, setRebookDay],
  );

  const handleWeekChange = useCallback(
    (date) => {
      const startOfDay = date.startOf('day');

      dispatch(setCurrentDay(startOfDay));
      dispatch(setView(dayView, startOfDay, stylistId));
      setRebookDay(startOfDay);
    },
    [dispatch, dayView, stylistId, setRebookDay],
  );

  const handleIconClick = useCallback(() => showRebook(false), [showRebook]);

  L.debug('render()', { showDatePicker, selectedDate, stylistId, pathname });

  return (
    <div data-bem="RebookAppointment" className="rebook-dpicker-input from-appointment-card">
      {showDatePicker && (
        <>
          <div role="button" className="close-style" tabIndex={0} onClick={handleIconClick}>
            <img src={ICO_CLOSE} alt="" />
          </div>
          <div className="picker-overlay" style={{ zIndex: '-1' }} />
          <TextField labelName={t('composite.rebookAppointmentDialog.selectDate')} htmlFor="rebookDayPicker">
            <div className="co-date-picker" id="rebookDayPicker">
              <DatePickerInput
                tag="RebookAppointment__common-date-picker"
                component={DatePickerInput.CommonDatePicker}
                inline
                selected={selectedDate}
                locale="en-GB"
                onOutsideClick={alwaysNoop}
                onChange={handleDateChange}
              />
              <DatePickerInput tag="RebookAppointment__week-component" component={DatePickerInput.WeeksComponent} onChangeDate={handleWeekChange} selectedDate={moment(selectedDate)} />
            </div>
          </TextField>
        </>
      )}
    </div>
  );
};
